/** API client + hooks TanStack pour l'identité réglementaire du cabinet (#3).
 *
 *  Alimente le DER (Document d'Entrée en Relation). Lecture pour tout
 *  utilisateur, écriture réservée aux ADMIN (403 côté API sinon). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { MissingItem } from "./regulatory-readiness";

export type MifidAdviceType = "INDEPENDENT" | "NON_INDEPENDENT";

export interface LegalProfileRead {
  id: string;
  organization_id: string;
  legal_form: string | null;
  siren: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  orias_number: string | null;
  orias_categories: string[];
  mifid_advice_type: MifidAdviceType | null;
  professional_association: string | null;
  rcp_insurer: string | null;
  rcp_coverage_amount: number | null;
  mediator_name: string | null;
  mediator_contact: string | null;
  remuneration_modes: string[];
  capital_links_disclosure: string | null;
  created_at: string;
  updated_at: string;
  /** Champs obligatoires manquants pour un DER — mêmes règles que le
   *  garde-fou de génération (#602). Absent d'une ancienne API. */
  missing?: MissingItem[];
}

export interface LegalProfileUpdate {
  legal_form?: string | null;
  siren?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  orias_number?: string | null;
  orias_categories?: string[];
  mifid_advice_type?: MifidAdviceType | null;
  professional_association?: string | null;
  rcp_insurer?: string | null;
  rcp_coverage_amount?: number | null;
  mediator_name?: string | null;
  mediator_contact?: string | null;
  remuneration_modes?: string[];
  capital_links_disclosure?: string | null;
}

export const legalProfileApi = {
  get: () => api.get<LegalProfileRead>("/organizations/me/legal-profile"),
  update: (payload: LegalProfileUpdate) =>
    api.put<LegalProfileRead>("/organizations/me/legal-profile", payload),
};

export const legalProfileKeys = {
  detail: ["legal-profile"] as const,
};

export function useLegalProfile() {
  return useQuery({
    queryKey: legalProfileKeys.detail,
    queryFn: () => legalProfileApi.get(),
  });
}

export function useUpdateLegalProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LegalProfileUpdate) => legalProfileApi.update(payload),
    onSuccess: (profile) => {
      qc.setQueryData(legalProfileKeys.detail, profile);
      void qc.invalidateQueries({ queryKey: legalProfileKeys.detail });
      void qc.invalidateQueries({ queryKey: ["der"] });
      void qc.invalidateQueries({ queryKey: ["adequation-pdf"] });
      void qc.invalidateQueries({ queryKey: ["regulatory-cockpit"] });
    },
  });
}
